/* ************************************************************************** */
/*                                Dependencies                                */
/* ************************************************************************** */

// Lib dependencies
import { useState, useCallback, useRef, useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { showNotification } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';

// Store
import useWorkspace from '@features/Workspace/store/hooks/useWorkspace';

// Helpers
import { getMaxUploadSize } from '@helpers/size';
import { getFileExtension } from '@app/shared/helpers/file';

// API & Types
import {
  QaResourceInput,
  useGetUploadSignedUrlLazyQuery,
  useInitializeMultipartUploadMutation,
  useGetMultipartPreSignedUrlsLazyQuery,
  useFinalizeMultipartUploadMutation,
} from '@app-types/blackroom-api';
import { validateUploadFileAsync } from '@app/shared/ui-kit/molecules/UploadArea/utils/validateUploadFile';
import {
  ignoredExtensions,
  ResourceMimeType,
} from '@app-types/blackroom-app/resourceMimeType';

/* ************************************************************************** */
/*                                   Types                                    */
/* ************************************************************************** */

export interface PendingQaFile {
  id: string;
  file: File;
  name: string;
  progress: number;
}

export interface UseQaFileUploadResult {
  uploadFiles: (files: File[]) => Promise<QaResourceInput[]>;
  isUploading: boolean;
  pendingFiles: PendingQaFile[];
  clearPendingFiles: () => void;
  cancelUpload: (fileId: string) => void;
}

/* ************************************************************************** */
/*                                    Hook                                    */
/* ************************************************************************** */

function useQaFileUpload(): UseQaFileUploadResult {
  /* ********************************** HOOKS ********************************* */

  // State
  const [isUploading, setIsUploading] = useState(false);
  const [pendingFiles, setPendingFiles] = useState<PendingQaFile[]>([]);

  // Refs for abort controllers
  const abortControllersRef = useRef<Map<string, AbortController>>(new Map());

  // Cleanup abort controllers on unmount
  useEffect(() => {
    return () => {
      abortControllersRef.current.forEach((controller) => controller.abort());
    };
  }, []);

  // Context
  const workspace = useWorkspace();

  // Localization
  const { t } = useTranslation(['Common', 'Resource']);

  // API
  const [getUploadSignedUrlQuery] = useGetUploadSignedUrlLazyQuery();
  const [initializeMultipartUpload] = useInitializeMultipartUploadMutation();
  const [finalizeMultipartUpload] = useFinalizeMultipartUploadMutation();
  const [getMultipartPreSignedUrls] = useGetMultipartPreSignedUrlsLazyQuery();

  /* **************************** INTERNAL METHODS **************************** */

  const uploadToS3 = useCallback(
    async (
      initialResourceId: string,
      fileKeyPrefix: string,
      fileBody: File,
      onProgress?: (progress: number) => void,
      signal?: AbortSignal,
    ): Promise<{ id: string; storagePath: string }> => {
      const chunkSize = 1024 * 1024 * 5; // 5MB
      const MAX_RETRIES = 2;
      const RETRY_DELAY = 1000;

      if (fileBody.size === 0) {
        throw new Error('File is empty');
      }

      // For large files, use multipart upload
      if (fileBody.size > chunkSize) {
        let lastError: Error | null = null;

        for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
          const id = attempt === 0 ? initialResourceId : uuidv4();
          const fileKey = `${fileKeyPrefix}/${id}/original`;

          try {
            const { data: initializeMultipartUploadData } =
              await initializeMultipartUpload({
                variables: { fileKey },
              });

            if (!initializeMultipartUploadData) {
              throw new Error(
                'Error during initialization of multipart upload',
              );
            }

            const partsCount = Math.ceil(fileBody.size / chunkSize);

            const { data: getMultipartPreSignedUrlsData } =
              await getMultipartPreSignedUrls({
                variables: {
                  fileKey:
                    initializeMultipartUploadData.initializeMultipartUpload
                      .fileKey,
                  uploadId:
                    initializeMultipartUploadData.initializeMultipartUpload
                      .uploadId,
                  parts: partsCount,
                },
              });

            if (!getMultipartPreSignedUrlsData) {
              throw new Error('Error during generation of presigned urls');
            }

            const numberOfParts =
              getMultipartPreSignedUrlsData.getMultipartPreSignedUrls.length;

            // Check if cancelled before starting uploads
            if (signal?.aborted) {
              throw new DOMException('Upload cancelled', 'AbortError');
            }

            let completedParts = 0;
            const uploadResults = await Promise.all(
              getMultipartPreSignedUrlsData.getMultipartPreSignedUrls.map(
                async (preSignedUrl, index) => {
                  const start = index * chunkSize;
                  const end = start + chunkSize;

                  for (
                    let partAttempt = 0;
                    partAttempt <= MAX_RETRIES;
                    partAttempt++
                  ) {
                    // Check if cancelled before each attempt
                    if (signal?.aborted) {
                      throw new DOMException('Upload cancelled', 'AbortError');
                    }

                    try {
                      const bodyChunk = fileBody.slice(start, end);
                      const uploadResult = await fetch(preSignedUrl, {
                        method: 'PUT',
                        body: bodyChunk,
                        signal,
                      });

                      if (!uploadResult.ok) {
                        throw new Error(
                          `Part upload failed with status: ${uploadResult.status}`,
                        );
                      }

                      completedParts++;
                      onProgress?.(
                        Math.round((completedParts / numberOfParts) * 100),
                      );

                      return uploadResult;
                    } catch (partError) {
                      // Re-throw AbortError immediately without retry
                      if ((partError as Error).name === 'AbortError') {
                        throw partError;
                      }
                      if (partAttempt === MAX_RETRIES) {
                        throw new Error(
                          `Failed to upload part ${index + 1} after ${MAX_RETRIES + 1} attempts`,
                        );
                      }
                      await new Promise((resolve) =>
                        setTimeout(resolve, RETRY_DELAY),
                      );
                    }
                  }
                  throw new Error(`Failed to upload part ${index + 1}`);
                },
              ),
            );

            const { data: finalizeMultipartUploadData } =
              await finalizeMultipartUpload({
                variables: {
                  finalizeMultipartUploadInput: {
                    fileKey:
                      initializeMultipartUploadData.initializeMultipartUpload
                        .fileKey,
                    uploadId:
                      initializeMultipartUploadData.initializeMultipartUpload
                        .uploadId,
                    parts: uploadResults.map((uploadResult, index) => ({
                      ETag: (uploadResult.headers.get('ETag') || '').replaceAll(
                        '"',
                        '',
                      ),
                      PartNumber: index + 1,
                    })),
                  },
                },
              });

            if (!finalizeMultipartUploadData?.finalizeMultipartUpload) {
              throw new Error('Error during finalization of multipart upload');
            }

            const pathUrlObject = new URL(
              finalizeMultipartUploadData.finalizeMultipartUpload,
            );
            const { origin } = pathUrlObject;

            return { id, storagePath: `${origin}/${fileKey}` };
          } catch (error) {
            // Re-throw AbortError immediately without retry
            if ((error as Error).name === 'AbortError') {
              throw error;
            }
            lastError = error as Error;
            if (attempt < MAX_RETRIES) {
              await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY));
              continue;
            }
          }
        }

        throw lastError || new Error('Could not upload file after all retries');
      }

      // For small files
      let lastError: Error | null = null;
      for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
        const id = attempt === 0 ? initialResourceId : uuidv4();
        const fileKey = `${fileKeyPrefix}/${id}/original`;

        try {
          const { data } = await getUploadSignedUrlQuery({
            variables: { fileKey },
          });

          if (!data?.resourceUploadSignedUrl) {
            throw new Error('Could not get upload URL');
          }

          const bucketResponse = await fetch(data.resourceUploadSignedUrl, {
            method: 'PUT',
            headers: { 'If-None-Match': '*' },
            body: fileBody,
            signal,
          });

          if (!bucketResponse.ok) {
            throw new Error(
              `Upload failed with status: ${bucketResponse.status}`,
            );
          }

          onProgress?.(100);

          const pathUrlObject = new URL(bucketResponse.url);
          const { origin, pathname } = pathUrlObject;

          return { id, storagePath: `${origin}${pathname}` };
        } catch (error) {
          // Re-throw AbortError immediately without retry
          if ((error as Error).name === 'AbortError') {
            throw error;
          }
          lastError = error as Error;
          if (attempt < MAX_RETRIES) {
            await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY));
            continue;
          }
        }
      }

      throw lastError || new Error('Could not upload file after all retries');
    },
    [
      getUploadSignedUrlQuery,
      initializeMultipartUpload,
      finalizeMultipartUpload,
      getMultipartPreSignedUrls,
    ],
  );

  /* **************************** EXPORTED METHODS **************************** */

  const uploadFiles = useCallback(
    async (files: File[]): Promise<QaResourceInput[]> => {
      if (files.length === 0) return [];

      const maxSize = getMaxUploadSize();
      const results: QaResourceInput[] = [];
      const newPendingFiles: PendingQaFile[] = [];

      // Validate and prepare files
      for (const file of files) {
        const id = uuidv4();
        const extension = getFileExtension(file.name);
        const validationResult = await validateUploadFileAsync(file);

        // Skip ignored extensions
        if (extension && ignoredExtensions.includes(extension)) {
          continue;
        }

        // Check file size
        if (file.size > maxSize) {
          showNotification({
            title: t(
              'Resource:Common.operation.upload.promptUpladSizeLimit.title',
            ),
            message: `${file.name} exceeds the maximum file size`,
            color: 'blackroomRed',
          });
          continue;
        }

        // Check empty files
        if (file.size === 0) {
          showNotification({
            title: t('Common:errors.genericTitle'),
            message: `${file.name} is empty`,
            color: 'blackroomRed',
          });
          continue;
        }

        // Check file type validation
        if (!validationResult.validation) {
          showNotification({
            title: t('Common:errors.genericTitle'),
            message: `${file.name} has an unsupported file type`,
            color: 'blackroomRed',
          });
          continue;
        }

        newPendingFiles.push({
          id,
          file,
          name: file.name,
          progress: 0,
        });
      }

      if (newPendingFiles.length === 0) return [];

      setIsUploading(true);
      setPendingFiles(newPendingFiles);

      // Upload files
      for (const pendingFile of newPendingFiles) {
        // Create abort controller for this upload
        const abortController = new AbortController();
        abortControllersRef.current.set(pendingFile.id, abortController);

        try {
          const resourceKeyPrefix = `${encodeURIComponent(workspace.tenant.id)}/${encodeURIComponent(
            workspace.id,
          )}/resources`;

          const uploadedObject = await uploadToS3(
            pendingFile.id,
            resourceKeyPrefix,
            pendingFile.file,
            (progress) => {
              setPendingFiles((prev) =>
                prev.map((pf) =>
                  pf.id === pendingFile.id ? { ...pf, progress } : pf,
                ),
              );
            },
            abortController.signal,
          );

          // Determine MIME type
          const extension = getFileExtension(pendingFile.file.name);
          let mimeType = pendingFile.file.type?.toLowerCase();
          const validationResult = await validateUploadFileAsync(
            pendingFile.file,
          );
          if (validationResult.detectedMimeType) {
            mimeType = validationResult.detectedMimeType;
          } else if (!pendingFile.file.type && extension) {
            mimeType = (ResourceMimeType as Record<string, string>)[extension];
          }

          results.push({
            ...uploadedObject,
            name: pendingFile.name,
            mimeType: mimeType || undefined,
            size: pendingFile.file.size,
          });
        } catch (error) {
          // Remove the file from pending files
          setPendingFiles((prev) =>
            prev.filter((pf) => pf.id !== pendingFile.id),
          );

          // Don't show error notification if upload was cancelled
          if ((error as Error).name === 'AbortError') {
            continue;
          }

          console.error(`Failed to upload ${pendingFile.name}:`, error);
          showNotification({
            title: t('Common:errors.genericTitle'),
            message: `Failed to upload ${pendingFile.name}`,
            color: 'blackroomRed',
          });
        } finally {
          // Clean up abort controller
          abortControllersRef.current.delete(pendingFile.id);
        }
      }

      setIsUploading(false);

      return results;
    },
    [workspace, uploadToS3, t],
  );

  const clearPendingFiles = useCallback(() => {
    setPendingFiles([]);
  }, []);

  const cancelUpload = useCallback((fileId: string) => {
    const controller = abortControllersRef.current.get(fileId);
    if (controller) {
      controller.abort();
      abortControllersRef.current.delete(fileId);
    }
  }, []);

  return {
    uploadFiles,
    isUploading,
    pendingFiles,
    clearPendingFiles,
    cancelUpload,
  };
}

export default useQaFileUpload;
